import {
  CONDITIONS,
  EMPTY_REGISTER_ITEM,
  EQUIPMENT_TYPES,
  LOOKUP_SOURCE_ASSET_TIGER,
  LOOKUP_SOURCE_REGISTER,
  STATUS_FILTERS,
} from './constants';

const LOOKUP_URL = process.env.REACT_APP_LOOKUP_URL || '/api/lookup';
const SUBMIT_URL = process.env.REACT_APP_SUBMIT_URL || '/api/submit';
const REGISTER_URL = process.env.REACT_APP_REGISTER_URL || '/api/register';

const REGISTER_FIELD_ALIASES = {
  asset_tag: ['asset_tag', 'assetTag', 'AssetTagNumber', 'Asset Tag Number', 'Asset_x0020_Tag_x0020_Number', 'Title'],
  brand_model: ['brand_model', 'brandModel', 'BrandandModel', 'Brand_x0020_and_x0020_Model'],
  serial_number: ['serial_number', 'serialNumber', 'SerialNumber', 'Serial', 'Serial_x0020_Number'],
  project_number: ['project_number', 'projectNumber', 'ProjectNumber', 'Project Number', 'Project_x0020_Number'],
  equipment_type: [
    'equipment_type', 'equipmentType', 'EquipmentType', 'EquipmentTypeValue',
    'Equipment', 'Equipment Type', 'Equipment_x0020_Type',
  ],
  condition: ['condition', 'Condition', 'ConditionValue'],
  country: ['country', 'Country'],
  assignee: ['assignee', 'assignee_name', 'AssigneeName', 'Assignee'],
  assignee_email: ['assignee_email', 'assigneeEmail', 'AssigneeEmail', 'Assignee_x0020_Email'],
  status: ['status', 'Status', 'StatusValue'],
  date_sent_out: ['date_sent_out', 'dateSentOut', 'DateSentOut', 'Date Sent Out', 'Date_x0020_Sent_x0020_Out'],
  date_returned: ['date_returned', 'dateReturned', 'DateReturned', 'Date Returned', 'Date_x0020_Returned'],
};

function asText(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return asText(value[0]);
  if (typeof value === 'object') {
    return asText(
      value.Value
      ?? value.value
      ?? value.LookupValue
      ?? value.Label
      ?? value.DisplayName
      ?? value.Title
      ?? value.Email
      ?? value.EMail
      ?? ''
    );
  }
  return String(value).trim();
}

function canonicalizeChoice(value, options) {
  const text = asText(value);
  if (!text) return '';
  return options.find((option) => option.toLowerCase() === text.toLowerCase()) || text;
}

function canonicalizeEquipmentType(value) {
  const exact = canonicalizeChoice(value, EQUIPMENT_TYPES);
  if (EQUIPMENT_TYPES.includes(exact)) return exact;

  const lower = asText(value).toLowerCase();
  if (!lower) return '';
  if (/\btablets?\b|\bipads?\b|\bsurface\s*(pro|go)\b/.test(lower)) return 'Tablet';
  if (/\blaptops?\b|\bnotebooks?\b|\bultrabooks?\b/.test(lower)) return 'Laptop';
  if (/\bphones?\b|\biphones?\b|\bsmartphones?\b|\bcellphones?\b|\bmobiles?\b/.test(lower)) return 'Phone';
  return 'Other';
}

function asPersonName(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    return asText(value.DisplayName || value.Title || value.Email || value.EMail || '');
  }
  return String(value);
}

function asPersonEmail(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    return asText(value.Email || value.EMail || '');
  }
  const text = String(value);
  return text.includes('@') ? text : '';
}

function fieldSources(raw) {
  const sources = [raw];
  if (raw.fields && typeof raw.fields === 'object') sources.push(raw.fields);
  if (raw.body && typeof raw.body === 'object' && !Array.isArray(raw.body)) sources.push(raw.body);
  return sources;
}

function pickField(raw, aliases, pattern) {
  for (const source of fieldSources(raw)) {
    for (const key of aliases) {
      if (source[key] != null) {
        const text = asText(source[key]);
        if (text) return text;
      }
    }

    const keys = Object.keys(source);
    for (const alias of aliases) {
      const found = keys.find((key) => key.toLowerCase() === alias.toLowerCase());
      if (!found) continue;
      const text = asText(source[found]);
      if (text) return text;
    }

    if (pattern) {
      for (const [key, value] of Object.entries(source)) {
        if (!pattern.test(key)) continue;
        const text = asText(value);
        if (text) return text;
      }
    }
  }

  return '';
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  let parsed = {};
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { result: text };
    }
  }

  if (!response.ok) {
    throw new Error(parsed.error || `Request failed (${response.status})`);
  }

  return parsed;
}

export function normalizeLookupResult(data) {
  if (!data || data.exists === false) {
    return { exists: false };
  }

  const fields = normalizeRegisterItem(data);
  const hasAssetData = Boolean(
    data.exists === true
    || fields.brand_model
    || fields.serial_number
    || fields.equipment_type
  );

  if (!hasAssetData) {
    return { exists: false };
  }

  return {
    exists: true,
    source: data.source || LOOKUP_SOURCE_REGISTER,
    asset_tag: fields.asset_tag,
    brand_model: fields.brand_model,
    serial_number: fields.serial_number,
    project_number: fields.project_number,
    equipment_type: fields.equipment_type,
    condition: fields.condition,
    country: fields.country,
    assignee: fields.assignee,
    assignee_email: fields.assignee_email,
    status: fields.status || 'Active',
  };
}

export function lookupAssetByTag(assetTag) {
  return postJson(LOOKUP_URL, { asset_tag: assetTag });
}

export function registerReflectsSubmission(payload, lookupData) {
  const result = normalizeLookupResult(lookupData);
  if (!result.exists || result.source === LOOKUP_SOURCE_ASSET_TIGER) return false;
  return payload?.action === 'checkin' && result.status === 'Returned';
}

export async function submitAsset(payload) {
  try {
    return await postJson(SUBMIT_URL, payload);
  } catch (error) {
    if (payload?.action === 'checkin' && payload.asset_tag) {
      try {
        const lookup = await lookupAssetByTag(payload.asset_tag);
        if (registerReflectsSubmission(payload, lookup)) {
          return { result: 'ok', confirmed: true };
        }
      } catch {
        // Keep the original submit error when lookup cannot confirm it.
      }
    }
    throw error;
  }
}

export function fetchRegister(filters = {}) {
  return postJson(REGISTER_URL, filters);
}

export function normalizeRegisterItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_REGISTER_ITEM };
  }

  const item = { ...EMPTY_REGISTER_ITEM };
  Object.entries(REGISTER_FIELD_ALIASES).forEach(([field, aliases]) => {
    const pattern = field === 'equipment_type' ? /equipment/i : undefined;
    item[field] = pickField(raw, aliases, pattern);
  });

  item.equipment_type = canonicalizeEquipmentType(item.equipment_type);
  item.condition = canonicalizeChoice(item.condition, CONDITIONS);
  item.status = canonicalizeChoice(item.status, STATUS_FILTERS);

  // SharePoint people fields arrive as { DisplayName, Email } objects.
  const person = raw.assignee ?? raw.Assignee ?? raw.assignee_name ?? raw.AssigneeName;
  item.assignee = asPersonName(person) || item.assignee;
  item.assignee_email = asPersonEmail(person) || item.assignee_email;
  if (!item.assignee && item.assignee_email) {
    item.assignee = item.assignee_email;
  }

  return item;
}

export function normalizeRegisterItems(data) {
  if (!data) return [];

  const collections = [data, data.items, data.value, data.result, data.body];
  const list = collections.find((candidate) => Array.isArray(candidate));
  if (!list) return [];

  return list.map(normalizeRegisterItem);
}
